"use client";

import type { I18n } from "@lingui/core";
import { Trans } from "@lingui/react/macro";
import { ExternalLinkIcon, LoaderIcon } from "lucide-react";
import Link from "next/link";
import React from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { serializeBigQuerySearchParams } from "../../settings/_search-params/bigquery-search-params";
import type { BigQuery } from "../_hooks/use-bigquery";

interface BigQuerySelectorProps {
  bigQueries: BigQuery[];
  selectedBigQueryId: string;
  isFetching: boolean;
  onBigQueryChange: (id: string) => void;
  i18n: I18n;
  workspaceId: string;
  onNavigateToSettings?: () => void;
}

export const BigQuerySelector = React.memo(function BigQuerySelector({
  bigQueries,
  selectedBigQueryId,
  isFetching,
  onBigQueryChange,
  i18n,
  workspaceId,
  onNavigateToSettings,
}: BigQuerySelectorProps) {
  const activeBigQueries = React.useMemo(
    () => bigQueries.filter((bq) => bq.isActive),
    [bigQueries],
  );
  const addBigQueryHref = serializeBigQuerySearchParams(
    `/${i18n.locale}/${encodeURIComponent(workspaceId)}/settings/ua-bigquery`,
    { addDataset: "1" },
  );

  if (isFetching) {
    return (
      <div className="space-y-2">
        <label
          htmlFor="bigquery"
          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
        >
          <Trans>BigQuery Configuration</Trans>
        </label>
        <div className="flex items-center justify-center h-9 border rounded-md">
          <LoaderIcon className="h-4 w-4 animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <label
        htmlFor="bigquery"
        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
      >
        <Trans>BigQuery Configuration</Trans>
      </label>
      <Select
        name="bigQueryId"
        value={selectedBigQueryId}
        onValueChange={onBigQueryChange}
        required
      >
        <SelectTrigger className="items-start text-start h-auto whitespace-normal [&>span]:line-clamp-4 [&>span]:whitespace-normal [&>span]:wrap-break-all">
          <SelectValue
            placeholder={i18n._("Select BigQuery")}
            className="text-left leading-5 wrap-break-all"
          />
        </SelectTrigger>
        <SelectContent>
          {activeBigQueries.length === 0 ? (
            <div className="p-3 text-sm space-y-2">
              <div className="text-muted-foreground">
                <Trans>No BigQuery configurations available</Trans>
              </div>
              <Link
                href={addBigQueryHref}
                className="inline-flex items-center gap-1.5 text-xs text-primary hover:text-primary/80 hover:underline"
                onClick={() => {
                  // Notify parent component to close the dialog
                  if (onNavigateToSettings) {
                    onNavigateToSettings();
                  }
                }}
              >
                <Trans>Set up BigQuery configuration</Trans>
                <ExternalLinkIcon className="h-3 w-3" />
              </Link>
            </div>
          ) : (
            activeBigQueries.map((bq) => (
              <SelectItem
                key={bq.bigQueryID}
                value={bq.bigQueryID}
                className="items-start whitespace-normal leading-5"
              >
                <div className="w-full wrap-break-words">{bq.name}</div>
              </SelectItem>
            ))
          )}
        </SelectContent>
      </Select>
    </div>
  );
});
